import { type RefObject, useEffect } from "react";

import { useIMask } from "react-imask";
import { twMerge } from "tailwind-merge";

import { formatting } from "@north-beam/nb-common";

import { TextInput, type TextInputProps } from "./text-input";

function formatValue(value: number | undefined, decimalPlaces?: number) {
  return value !== undefined
    ? formatting.formatDecimal(value, { mantissa: decimalPlaces })
    : "";
}

export interface FormattedNumberInputProps
  extends Omit<TextInputProps, "value" | "onChange" | "defaultValue"> {
  value: number | undefined;
  defaultValue?: number;
  onChange: (value: number | undefined) => void;
  decimalPlaces?: number;
}

export const FormattedNumberInput = ({
  className,
  value,
  defaultValue,
  onChange,
  decimalPlaces = 2,
  ...props
}: FormattedNumberInputProps) => {
  const {
    ref,
    unmaskedValue,
    value: maskedValue,
    setUnmaskedValue,
  } = useIMask({
    mask: Number,
    scale: decimalPlaces,
    thousandsSeparator: ",",
    radix: ".",
    signed: true,
    normalizeZeros: true,
    padFractionalZeros: true,
    min: undefined,
    max: undefined,
  });

  useEffect(() => {
    if (value !== undefined) {
      setUnmaskedValue(String(value));
    }
  }, [value, setUnmaskedValue]);

  return (
    <TextInput
      ref={ref as RefObject<HTMLInputElement>}
      className={twMerge("text-xs text-right font-mono", className)}
      defaultValue={
        defaultValue !== undefined
          ? formatValue(defaultValue, decimalPlaces)
          : ""
      }
      {...props}
      value={maskedValue}
      onBlur={() => {
        onChange(Number(unmaskedValue));
      }}
    />
  );
};
